import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, NOMBRES, conditions, excelDate, seededRandom } from "../spec";

const SHEET = "BR Directorio";
const DEPTOS = ["Ventas", "Finanzas", "TI", "Operaciones", "Recursos Humanos"] as const;

const usuario = (nombre: string) =>
  nombre.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(" ", ".");

const rnd = seededRandom(3434);
// ID, Nombre, Departamento, Extensión, Correo
const DIRECTORIO = NOMBRES.slice(0, 30).map((nombre, i) => [
  `EMP-${1001 + i}`,
  nombre,
  DEPTOS[i % DEPTOS.length],
  2100 + i * 7,
  `${usuario(nombre)}@empresa.mx`,
] as [string, string, string, number, string]);

// Fecha, Extensión, Minutos
const LLAMADAS = Array.from({ length: 20 }, () => [
  excelDate("2026-03-02") + rnd.int(0, 20),
  rnd.pick(DIRECTORIO)[3],
  rnd.int(1, 45),
] as [number, number, number]).sort((a, b) => a[0] - b[0]);

const LINEAS = ["Software", "Hardware", "Servicios"] as const;
// Región, Software, Hardware, Servicios
const TASAS: [string, number, number, number][] = [
  ["Norte", 0.08, 0.05, 0.1],
  ["Sur", 0.07, 0.04, 0.09],
  ["Centro", 0.09, 0.06, 0.11],
  ["Occidente", 0.075, 0.045, 0.095],
];
// Ventas desde, Bono — ascending, as approximate COINCIDIR requires.
const BONOS: [number, number][] = [
  [0, 0],
  [100000, 1500],
  [250000, 4000],
  [500000, 9000],
  [800000, 15000],
];

const IN = {
  nombre: "Daniela Ortiz",
  posicion: 7,
  extension: 2100 + 17 * 7,
  region: "Centro",
  linea: "Servicios" as (typeof LINEAS)[number],
  ventas: 312000,
};

const posicion = DIRECTORIO.findIndex((r) => r[1] === IN.nombre) + 1;
const porExtension = (ext: number) => DIRECTORIO.find((r) => r[3] === ext)!;
const bono = [...BONOS].reverse().find(([desde]) => IN.ventas >= desde)![1];

export const spec: FuncionProjectSpec = {
  projectId: "funciones-indice-coincidir",
  sheets: [
    {
      name: SHEET,
      title: "Directorio corporativo",
      subtitle: "Directorio, registro de llamadas y tablas de comisiones — responde en las celdas amarillas",
      tables: [
        {
          name: "Directorio",
          anchor: "A4",
          headers: ["ID", "Nombre", "Departamento", "Extensión", "Correo"],
          rows: DIRECTORIO,
        },
        {
          name: "Llamadas",
          anchor: "G4",
          headers: ["Fecha", "Extensión", "Minutos"],
          rows: LLAMADAS,
          answerColumns: ["Empleado"],
          formats: { Fecha: "dd/mm/yyyy" },
        },
        {
          name: "Tasas",
          anchor: "L4",
          headers: ["Región", ...LINEAS],
          rows: TASAS,
          formats: { Software: "0.0%", Hardware: "0.0%", Servicios: "0.0%" },
        },
        {
          name: "Bonos",
          anchor: "L11",
          headers: ["Ventas desde", "Bono"],
          rows: BONOS,
          formats: { "Ventas desde": "$#,##0", Bono: "$#,##0" },
        },
      ],
      inputs: [
        { address: "R4", label: "Nombre", value: IN.nombre },
        { address: "R5", label: "Posición", value: IN.posicion },
        { address: "R6", label: "Extensión", value: IN.extension },
        { address: "R7", label: "Región", value: IN.region },
        { address: "R8", label: "Línea", value: IN.linea },
        { address: "R9", label: "Ventas del mes", value: IN.ventas, format: "$#,##0" },
      ],
      answers: [
        { address: "R12", label: "Tarea 1 · Posición del nombre" },
        { address: "R13", label: "Tarea 2 · Correo en la posición" },
        { address: "R14", label: "Tarea 3 · Correo del nombre" },
        { address: "R15", label: "Tarea 4 · ID de la extensión" },
        { address: "R16", label: "Tarea 6 · Tasa de comisión", format: "0.0%" },
        { address: "R17", label: "Tarea 7 · Bono del mes", format: "$#,##0" },
      ],
    },
  ],
  checks: {
    1: { kind: "cell", sheet: SHEET, address: "R12", fns: ["MATCH"], expected: posicion },
    2: { kind: "cell", sheet: SHEET, address: "R13", fns: ["INDEX"], expected: DIRECTORIO[IN.posicion - 1][4] },
    3: { kind: "cell", sheet: SHEET, address: "R14", fns: ["INDEX", "MATCH"], expected: DIRECTORIO[posicion - 1][4] },
    4: { kind: "cell", sheet: SHEET, address: "R15", fns: ["INDEX", "MATCH"], expected: porExtension(IN.extension)[0] },
    5: {
      kind: "column", sheet: SHEET, table: "Llamadas", column: "Empleado", fns: ["INDEX", "MATCH"],
      expected: LLAMADAS.map((l) => porExtension(l[1])[1]),
    },
    6: {
      kind: "cell", sheet: SHEET, address: "R16", fns: ["INDEX", "MATCH"], minCounts: { MATCH: 2 },
      expected: TASAS.find((t) => t[0] === IN.region)![1 + LINEAS.indexOf(IN.linea)],
    },
    7: { kind: "cell", sheet: SHEET, address: "R17", fns: ["INDEX", "MATCH"], expected: bono },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-indice-coincidir",
  title: "INDICE y COINCIDIR: Directorio corporativo",
  subtitle: "La combinación que busca en cualquier dirección",
  description:
    "Administras el directorio de la empresa: 30 empleados, el registro de llamadas por extensión y las tablas de comisiones y bonos.\n\nCOINCIDIR dice en qué posición está un valor; INDICE devuelve lo que hay en una posición. Juntas buscan hacia la izquierda, en filas y columnas a la vez, y funcionan en cualquier versión de Excel.",
  topics: [
    "COINCIDIR: la posición de un valor",
    "INDICE: el valor de una posición",
    "INDICE + COINCIDIR como búsqueda completa",
    "Buscar hacia la izquierda",
    "Búsqueda en dos sentidos (fila y columna)",
    "Coincidencia aproximada con tipo 1",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: ¿En qué posición está?",
      description:
        "En la hoja <strong>BR Directorio</strong>, celda <strong>R12</strong>: devuelve en qué <strong>posición</strong> de la columna Nombre del <strong>Directorio</strong> está el nombre de <strong>R4</strong>.",
      solution: [
        `Selecciona ${F("R12")}.`,
        `COINCIDIR(valor, rango, 0) devuelve la posición del valor dentro del rango; el ${F("0")} pide coincidencia exacta.`,
        `Escribe ${F("=COINCIDIR(R4,Directorio[Nombre],0)")}.`,
      ],
      expectedResult: "Un número de posición",
      finalFormula: "=COINCIDIR(R4,Directorio[Nombre],0)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: ¿Qué hay en esa posición?",
      description:
        "En <strong>R13</strong>: devuelve el <strong>Correo</strong> que está en la posición indicada en <strong>R5</strong> de la columna Correo.",
      solution: [
        `Selecciona ${F("R13")}.`,
        "INDICE(rango, posición) devuelve el elemento que está en esa posición.",
        `Escribe ${F("=INDICE(Directorio[Correo],R5)")}.`,
      ],
      expectedResult: "El 7.º correo del directorio",
      finalFormula: "=INDICE(Directorio[Correo],R5)",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Juntas: el correo de una persona",
      description:
        "En <strong>R14</strong>: devuelve el <strong>Correo</strong> de la persona de <strong>R4</strong>, combinando INDICE y COINCIDIR.",
      solution: [
        `Selecciona ${F("R14")}.`,
        "COINCIDIR encuentra la posición del nombre y INDICE usa esa posición en la columna Correo.",
        `Escribe ${F("=INDICE(Directorio[Correo],COINCIDIR(R4,Directorio[Nombre],0))")}.`,
      ],
      expectedResult: "Correo de Daniela Ortiz",
      finalFormula: "=INDICE(Directorio[Correo],COINCIDIR(R4,Directorio[Nombre],0))",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Buscar hacia la izquierda",
      description:
        "En <strong>R15</strong>: devuelve el <strong>ID</strong> del empleado con la extensión de <strong>R6</strong>. ID está a la <strong>izquierda</strong> de Extensión: BUSCARV no podría hacerlo.",
      solution: [
        `Selecciona ${F("R15")}.`,
        "Con INDICE + COINCIDIR la columna donde buscas y la que devuelves son independientes.",
        `Escribe ${F("=INDICE(Directorio[ID],COINCIDIR(R6,Directorio[Extensión],0))")}.`,
      ],
      expectedResult: "ID del empleado de esa extensión",
      finalFormula: "=INDICE(Directorio[ID],COINCIDIR(R6,Directorio[Extensión],0))",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: ¿Quién hizo cada llamada?",
      description:
        "En la tabla <strong>Llamadas</strong> (G4), columna <strong>Empleado</strong>: devuelve el <strong>Nombre</strong> del empleado de cada <strong>Extensión</strong>.",
      solution: [
        `Selecciona ${F("J5")}.`,
        `Escribe ${F("=INDICE(Directorio[Nombre],COINCIDIR([@Extensión],Directorio[Extensión],0))")}.`,
      ],
      expectedResult: "Nombre del empleado de cada llamada",
      finalFormula: "=INDICE(Directorio[Nombre],COINCIDIR([@Extensión],Directorio[Extensión],0))",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Tasa por región y línea",
      description: conditions(
        "En <strong>R16</strong>: devuelve la tasa de comisión de la tabla <strong>Tasas</strong> (L4) para la región de <strong>R7</strong> y la línea de <strong>R8</strong>. Necesitas <strong>dos COINCIDIR</strong>:",
        [
          "Uno busca la <strong>región</strong> en la columna Región → número de <strong>fila</strong>",
          "Otro busca la <strong>línea</strong> en los encabezados Software, Hardware, Servicios → número de <strong>columna</strong>",
          "INDICE recibe el bloque de tasas, la fila y la columna",
        ]
      ),
      solution: [
        `Selecciona ${F("R16")}.`,
        `El bloque de tasas es ${F("Tasas[[Software]:[Servicios]]")} y sus encabezados son ${F("Tasas[[#Encabezados],[Software]:[Servicios]]")}.`,
        `Escribe ${F("=INDICE(Tasas[[Software]:[Servicios]],COINCIDIR(R7,Tasas[Región],0),COINCIDIR(R8,Tasas[[#Encabezados],[Software]:[Servicios]],0))")}.`,
        "Cambia R7 o R8 y verifica el cruce en la tabla.",
      ],
      expectedResult: "11.0% (Centro × Servicios)",
      finalFormula:
        "=INDICE(Tasas[[Software]:[Servicios]],COINCIDIR(R7,Tasas[Región],0),COINCIDIR(R8,Tasas[[#Encabezados],[Software]:[Servicios]],0))",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Bono por tramo de ventas",
      description: conditions(
        "En <strong>R17</strong>: devuelve el <strong>Bono</strong> que corresponde a las ventas de <strong>R9</strong> según la tabla <strong>Bonos</strong> (L11). Cada bono aplica <strong>desde</strong> su monto:",
        ["Las ventas casi nunca coinciden con un monto de la tabla", "Toma el tramo inmediato inferior"]
      ),
      solution: [
        `Selecciona ${F("R17")}.`,
        `Con tipo de coincidencia ${F("1")}, COINCIDIR devuelve la posición del valor más grande que sea menor o igual. La tabla debe estar ordenada de menor a mayor.`,
        `Escribe ${F("=INDICE(Bonos[Bono],COINCIDIR(R9,Bonos[Ventas desde],1))")}.`,
      ],
      expectedResult: "$4,000",
      finalFormula: "=INDICE(Bonos[Bono],COINCIDIR(R9,Bonos[Ventas desde],1))",
    },
  ],
};
